import { AttachFile, Send } from '@mui/icons-material';
import {
    Alert,
    Box,
    Breadcrumbs,
    Button,
    Chip,
    Divider,
    FormControl,
    InputLabel,
    Link,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
} from '@mui/material';
import React, { useCallback, useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate, useParams } from 'react-router-dom';
import Swal from 'sweetalert2';
import api from '../../api';
import Loading from '../../components/Loading';
import { isMasterUser, patchStoredUser } from '../../helpers/authSession';
import { formatBytes, ticketStatusColor, TICKET_ACCEPT, filesToTicketPayload, isTicketClosed, ticketChannelLabel } from './ticketHelpers';

const MAX_FILE_BYTES = 5 * 1024 * 1024;
const MAX_FILES = 5;

function formatDate(value) {
    if (!value) return '—';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '—';
    return date.toLocaleString('pt-BR');
}

function AttachmentList({ attachments }) {
    if (!attachments?.length) return null;
    return (
        <Stack spacing={0.5} mt={1}>
            {attachments.map((att) => (
                <Stack key={att.id} direction="row" spacing={1} alignItems="center">
                    <AttachFile fontSize="small" color="action" />
                    {att.url ? (
                        <Link href={att.url} target="_blank" rel="noopener noreferrer" variant="body2">
                            {att.file_name}
                        </Link>
                    ) : (
                        <Typography variant="body2">{att.file_name}</Typography>
                    )}
                    <Typography variant="caption" color="text.secondary">
                        ({formatBytes(att.size_bytes)})
                    </Typography>
                </Stack>
            ))}
        </Stack>
    );
}

function TicketDetailPage() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isMaster, setIsMaster] = useState(isMasterUser());
    const [ticket, setTicket] = useState(null);
    const [statuses, setStatuses] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reply, setReply] = useState('');
    const [files, setFiles] = useState([]);
    const [submitting, setSubmitting] = useState(false);
    const [statusSaving, setStatusSaving] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        api.get('/auth/me')
            .then((response) => {
                const user = response?.data?.data;
                if (user && typeof user.is_master === 'boolean') {
                    patchStoredUser({ is_master: user.is_master });
                    setIsMaster(user.is_master);
                }
            })
            .catch(() => {});
        api.get('/tickets/meta')
            .then((response) => {
                setStatuses(Array.isArray(response?.data?.statuses) ? response.data.statuses : []);
            })
            .catch(() => setStatuses([]));
    }, []);

    const loadTicket = useCallback(() => {
        setLoading(true);
        setError('');
        api.get(`/tickets/${id}`)
            .then((response) => {
                setTicket(response?.data?.data || null);
            })
            .catch((err) => {
                setTicket(null);
                setError(err?.response?.data?.message || 'Ticket não encontrado.');
            })
            .finally(() => setLoading(false));
    }, [id]);

    useEffect(() => {
        loadTicket();
    }, [loadTicket]);

    const onFilesChange = (event) => {
        const selected = Array.from(event.target.files || []);
        event.target.value = '';
        const next = [...files];
        for (const file of selected) {
            if (next.length >= MAX_FILES) break;
            if (file.size > MAX_FILE_BYTES) {
                Swal.fire({ icon: 'warning', title: 'Arquivo grande', text: `${file.name} excede 5 MB.` });
                continue;
            }
            next.push(file);
        }
        setFiles(next);
    };

    const handleReply = async (event) => {
        event.preventDefault();
        setSubmitting(true);
        try {
            const payload = {
                body: reply.trim(),
                files: await filesToTicketPayload(files),
            };
            const response = await api.post(`/tickets/${id}/messages`, payload);
            setTicket(response?.data?.data || null);
            setReply('');
            setFiles([]);
        } catch (err) {
            const data = err?.response?.data;
            await Swal.fire({
                icon: 'error',
                title: 'Falha ao responder',
                text: data?.errors?.body || data?.errors?.files || data?.message || 'Tente novamente.',
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleStatusChange = async (status) => {
        setStatusSaving(true);
        try {
            const response = await api.put(`/tickets/${id}/status`, { status });
            setTicket(response?.data?.data || null);
        } catch (err) {
            await Swal.fire({
                icon: 'error',
                title: 'Falha ao atualizar status',
                text: err?.response?.data?.message || 'Tente novamente.',
            });
        } finally {
            setStatusSaving(false);
        }
    };

    if (loading) return <Loading />;

    if (!ticket) {
        return (
            <Box>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || 'Ticket não encontrado.'}
                </Alert>
                <Button onClick={() => navigate('/admin/tickets')}>Voltar</Button>
            </Box>
        );
    }

    const closed = isTicketClosed(ticket.status);

    return (
        <Box maxWidth={900}>
            <Breadcrumbs sx={{ mb: 2 }}>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin">
                    Admin
                </Link>
                <Link component={RouterLink} underline="hover" color="inherit" to="/admin/tickets">
                    Tickets
                </Link>
                <Typography color="text.primary" noWrap sx={{ maxWidth: 240 }}>
                    {ticket.subject}
                </Typography>
            </Breadcrumbs>

            <Stack spacing={1} mb={3}>
                <Typography variant="h5" fontWeight={700}>
                    {ticket.subject}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    <Chip label={ticketChannelLabel(ticket.channel, ticket.channel_label)} variant="outlined" />
                    <Chip label={ticket.category_label || ticket.category} />
                    <Chip label={ticket.status_label || ticket.status} color={ticketStatusColor(ticket.status)} />
                </Stack>
                <Typography variant="body2" color="text.secondary">
                    Aberto por {ticket.user_name} ({ticket.user_email}) · {formatDate(ticket.created_at)}
                </Typography>
            </Stack>

            {isMaster ? (
                <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
                    <FormControl fullWidth size="small">
                        <InputLabel id="ticket-status-label">Status</InputLabel>
                        <Select
                            labelId="ticket-status-label"
                            label="Status"
                            value={ticket.status || ''}
                            disabled={statusSaving}
                            onChange={(e) => handleStatusChange(e.target.value)}
                        >
                            {statuses.map((item) => (
                                <MenuItem key={item.code} value={item.code}>
                                    {item.label}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Paper>
            ) : null}

            <Stack spacing={2} mb={3}>
                {(ticket.messages || []).map((message) => (
                    <Paper
                        key={message.id}
                        variant="outlined"
                        sx={{
                            p: 2,
                            bgcolor: message.is_staff ? 'action.hover' : 'background.paper',
                        }}
                    >
                        <Stack direction="row" justifyContent="space-between" spacing={2} mb={1}>
                            <Typography variant="subtitle2">
                                {message.author_name}
                                {message.is_staff ? ' · Equipe' : ''}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                                {formatDate(message.created_at)}
                            </Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {message.body}
                        </Typography>
                        <AttachmentList attachments={message.attachments} />
                    </Paper>
                ))}
            </Stack>

            <Divider sx={{ mb: 3 }} />

            {closed ? (
                <Alert severity="info">
                    Este ticket foi encerrado e não aceita novas mensagens.
                </Alert>
            ) : (
                <Box component="form" onSubmit={handleReply}>
                    <Typography variant="h6" mb={1}>
                        Responder
                    </Typography>
                    <TextField
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        multiline
                        minRows={4}
                        fullWidth
                        placeholder="Escreva sua mensagem…"
                        sx={{ mb: 1.5 }}
                    />
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1} alignItems={{ sm: 'center' }} mb={1}>
                        <Button variant="outlined" component="label" startIcon={<AttachFile />} disabled={submitting}>
                            Anexar
                            <input hidden type="file" multiple accept={TICKET_ACCEPT} onChange={onFilesChange} />
                        </Button>
                        <Button type="submit" variant="contained" endIcon={<Send />} disabled={submitting}>
                            {submitting ? 'Enviando…' : 'Enviar'}
                        </Button>
                    </Stack>
                    <Stack spacing={0.5}>
                        {files.map((file, index) => (
                            <Stack key={`${file.name}-${index}`} direction="row" spacing={1} alignItems="center">
                                <Typography variant="body2">
                                    {file.name} ({formatBytes(file.size)})
                                </Typography>
                                <Button size="small" onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}>
                                    Remover
                                </Button>
                            </Stack>
                        ))}
                    </Stack>
                </Box>
            )}
        </Box>
    );
}

export default TicketDetailPage;
